/* eslint-disable curly */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';
// Removed API import - will use dashboard data instead

const SiteCodeSelector = ({
  selectedSiteCodes = [],
  onSiteCodesChange,
  maxSelections = null,
  disabled = false,
  dashboardData = [], // Add dashboard data prop
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [manualEntry, setManualEntry] = useState('');
  const [entryMode, setEntryMode] = useState('search'); // 'search' or 'manual'
  const [error, setError] = useState('');

  // Debounced search function using dashboard data
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchValue) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (searchValue && searchValue.trim().length >= 2) {
            setLoading(true);
            setError('');
            try {
              // Filter dashboard data based on search term
              const filteredData = dashboardData
                .filter((site) => {
                  const siteCode = site.site_code || '';
                  const projectName = site.project_name || '';
                  const technicalCity = site.technical_city || '';
                  const region = site.region || '';

                  const searchLower = searchValue.toLowerCase();
                  return (
                    siteCode.toLowerCase().includes(searchLower) ||
                    projectName.toLowerCase().includes(searchLower) ||
                    technicalCity.toLowerCase().includes(searchLower) ||
                    region.toLowerCase().includes(searchLower)
                  );
                })
                .slice(0, 100); // Limit to 100 results for performance

              setOptions(filteredData);
            } catch (err) {
              console.error('Error searching site codes:', err);
              setError('Failed to search site codes. Please try again.');
              setOptions([]);
            } finally {
              setLoading(false);
            }
          } else {
            setOptions([]);
          }
        }, 300);
      };
    })(),
    [dashboardData]
  );

  // Handle search input changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Handle manual entry parsing
  const handleManualEntry = () => {
    if (!manualEntry.trim()) return;

    const siteCodes = manualEntry
      .split(/[,\n]/)
      .map((code) => code.trim())
      .filter((code) => code.length > 0);

    if (siteCodes.length === 0) {
      setError('Please enter valid site codes separated by commas or new lines.');
      return;
    }

    // Validate site codes (basic validation)
    const invalidCodes = siteCodes.filter((code) => !/^[A-Z0-9_-]+$/i.test(code));
    if (invalidCodes.length > 0) {
      setError(`Invalid site codes: ${invalidCodes.join(', ')}`);
      return;
    }

    // Add new site codes to selection
    const newSiteCodes = siteCodes.map((code) => ({
      site_code: code,
      id: `manual_${code}`,
      project_name: 'Manual Entry',
      status: 'Unknown',
      technical_city: 'Unknown',
      region: 'Unknown',
    }));

    const updatedSelection = [...selectedSiteCodes, ...newSiteCodes];

    // Remove duplicates
    const uniqueSelection = updatedSelection.filter(
      (site, index, self) => index === self.findIndex((s) => s.site_code === site.site_code)
    );

    // Check max selections limit
    if (maxSelections && uniqueSelection.length > maxSelections) {
      setError(`Maximum ${maxSelections} site codes allowed.`);
      return;
    }

    onSiteCodesChange(uniqueSelection);
    setManualEntry('');
    setError('');
  };

  // Handle site code removal
  const handleRemoveSiteCode = (siteCodeToRemove) => {
    const updatedSelection = selectedSiteCodes.filter(
      (site) => site.site_code !== siteCodeToRemove
    );
    onSiteCodesChange(updatedSelection);
  };

  // Handle autocomplete selection
  const handleAutocompleteChange = (event, newValue) => {
    if (maxSelections && newValue.length > maxSelections) {
      setError(`Maximum ${maxSelections} site codes allowed.`);
      return;
    }
    onSiteCodesChange(newValue);
    setError('');
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Entry Mode Selector */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Entry Mode</InputLabel>
        <Select value={entryMode} onChange={(e) => setEntryMode(e.target.value)} label="Entry Mode">
          <MenuItem value="search">Search & Select</MenuItem>
        </Select>
      </FormControl>

      {entryMode === 'search' ? (
        /* Search Mode */
        <Box>
          <Autocomplete
            multiple
            disabled={disabled}
            options={options}
            value={selectedSiteCodes}
            onChange={handleAutocompleteChange}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              setSearchTerm(newInputValue);
            }}
            inputValue={inputValue}
            getOptionLabel={(option) => option.site_code}
            isOptionEqualToValue={(option, value) => option.site_code === value.site_code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Site Codes"
                placeholder="Type to search site codes..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.site_code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.project_name} • {option.status} • {option.technical_city}
                  </Typography>
                </Box>
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.site_code}
                  label={option.site_code}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            noOptionsText={
              loading
                ? 'Searching...'
                : searchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No site codes found'
            }
            loading={loading}
            loadingText="Searching site codes..."
            ListboxProps={{
              style: {
                maxHeight: '200px',
              },
            }}
          />
        </Box>
      ) : (
        /* Manual Entry Mode */
        <Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Manual Site Code Entry"
            placeholder="Enter site codes separated by commas or new lines:&#10;SITE001, SITE002&#10;SITE003&#10;SITE004"
            value={manualEntry}
            onChange={(e) => setManualEntry(e.target.value)}
            disabled={disabled}
            helperText="Enter site codes separated by commas or new lines"
          />
          <Button
            variant="outlined"
            onClick={handleManualEntry}
            disabled={!manualEntry.trim() || disabled}
            sx={{ mt: 1 }}
            size="small"
          >
            Add Site Codes
          </Button>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Selected Site Codes Display */}
      {selectedSiteCodes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Selected Site Codes ({selectedSiteCodes.length})
            {maxSelections && ` / ${maxSelections} max`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedSiteCodes.map((site) => (
              <Chip
                key={site.site_code}
                label={site.site_code}
                onDelete={() => handleRemoveSiteCode(site.site_code)}
                color="primary"
                variant="filled"
                size="small"
                disabled={disabled}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Quick Actions */}
      {selectedSiteCodes.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onSiteCodesChange([])}
            disabled={disabled}
          >
            Clear All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const siteCodesText = selectedSiteCodes.map((site) => site.site_code).join(', ');
              navigator.clipboard.writeText(siteCodesText);
            }}
            disabled={disabled}
          >
            Copy to Clipboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SiteCodeSelector;
